import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import { Box, LabelPosition } from '@sorare/core/src/atoms/ui/Box';
import { ANY_SPORT_SCOUTING_CENTER } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { scoutingCenter } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import binoculars from './assets/binoculars.png';
import shadow from './assets/shadow.png';

const NoPaddingBox = styled(Box)`
  --light-green: #bff6d6;
  --dark-green: #048a3d;

  position: relative;
  padding: 0;
  overflow: hidden;
  border: 2px solid transparent;
  background:
    linear-gradient(to right, black, black) padding-box,
    linear-gradient(150deg, var(--light-green), var(--dark-green)) border-box;
`;
const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  opacity: 0;
  background: linear-gradient(
    to bottom,
    transparent 80%,
    var(--dark-green) 150%
  );

  ${NoPaddingBox}:hover & {
    opacity: 1;
  }
  transition: opacity 0.2s ease-in-out;
`;
const Background = styled(Vertical)`
  --tile-size: 44px;
  --offset: calc(var(--tile-size) / 2);
  background: linear-gradient(to bottom, transparent, var(--c-black) 80%),
    repeating-linear-gradient(
      to bottom,
      var(--dark-green) var(--offset),
      transparent calc(var(--offset) + 1px),
      transparent calc(var(--offset) + var(--tile-size))
    ),
    repeating-linear-gradient(
      to right,
      var(--dark-green) var(--offset),
      transparent calc(var(--offset) + 1px),
      transparent calc(var(--offset) + var(--tile-size))
    ),
    linear-gradient(to bottom, #3ddc8435, #3ddc8400);

  gap: 0;
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    flex-direction: column;
    gap: var(--quadruple-unit);
  }
`;
const ImgsWrapper = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  margin-bottom: calc(-1 * var(--triple-unit));

  margin-left: auto;
  @media ${tabletAndAbove} {
    margin: auto;
    width: 200px;
    height: 200px;
  }
`;
const Img = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
`;
const Shadow = styled(Img)`
  ${NoPaddingBox}:hover & {
    opacity: 0.5;
  }
  transition: opacity 0.2s ease-in-out;
`;
const Binoculars = styled(Img)`
  will-change: transform;

  ${NoPaddingBox}:hover & {
    transform: translateY(-15px) rotate(10deg);
    transition: transform 0.2s cubic-bezier(0.55, 0, 0.94, 0.52);
  }
  transition: transform 0.1s cubic-bezier(0.56, 0.01, 1, 1);
`;
const HiddenOnMobile = styled(Vertical).attrs({ gap: 2 })`
  display: none;

  @media ${tabletAndAbove} {
    display: flex;
  }
`;
const LeftAlignedButton = styled(Button)`
  align-self: flex-start;
`;

export const ScoutingCenterTile = () => {
  const { sport } = useSportContext();

  return (
    <ButtonBase
      to={generateSportPath(ANY_SPORT_SCOUTING_CENTER, {
        sport,
      })}
    >
      <NoPaddingBox
        labelProps={{
          color: 'var(--c-black)',
          bgColor: '#bff6d6',
          position: LabelPosition.LEFT,
          value: (
            <LabelM uppercase bold>
              <FormattedMessage {...scoutingCenter.scout} />
            </LabelM>
          ),
        }}
      >
        <Overlay />
        <Background>
          <ImgsWrapper>
            <Shadow src={shadow} alt="" />
            <Binoculars src={binoculars} alt="" />
          </ImgsWrapper>
          <Vertical gap={0.5}>
            <HeadlineM>
              <FormattedMessage
                id="ScoutingCenterTile.title"
                defaultMessage="Scouting Center"
              />
            </HeadlineM>
            <HiddenOnMobile>
              <BodyM color="var(--c-nd-700)">
                <FormattedMessage
                  id="ScoutingCenterTile.description"
                  defaultMessage="Discover, analyze and sign players to improve your teams."
                />
              </BodyM>
              <LeftAlignedButton size="medium" color="secondary" asSpan>
                <FormattedMessage
                  id="ScoutingCenterTile.cta.text"
                  defaultMessage="Scout Now"
                />
              </LeftAlignedButton>
            </HiddenOnMobile>
          </Vertical>
        </Background>
      </NoPaddingBox>
    </ButtonBase>
  );
};
